import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { AppLangs } from '../i18n';
import { Rounded } from '../theme/Rounded';
import { Table, TableProps, TableRowSelection } from '../utils/local-antd';
import { Flex } from './Flex';

interface AtiraTableProps<T extends Record<string, any>>
  extends Omit<TableProps<T>, 'dataSource'> {
  data: T[];
  columns: TableProps<T>['columns'];
  loading?: boolean;
  selectedRowKeys?: React.Key[];
  onSelectedRowKeysChange?: ATVoidFunction<React.Key[]>;
  size?: 'small';
}

const StyledTableWrapper = styled.div<{ size?: 'small' }>`
  .ant-table-wrapper {
    border-radius: ${Rounded.md};
    border-color: ${({ theme }) => theme.danger} !important;
  }

  .ant-table-wrapper * {
    border-color: ${({ theme }) => theme.bdSubtle} !important;
  }

  .ant-table-cell-fix-left,
  .ant-table-cell-fix-right {
    border-inline-start: 0 !important;
    border-inline-end-color: ${({ theme }) => theme.fgPrimary} !important;
    border-inline-end-width: 2px !important;
  }

  .ant-table-placeholder,
  .ant-table-placeholder:hover .ant-table-cell {
    background: ${({ theme }) => theme.bgCanvas} !important;
    color: ${({ theme }) => theme.fgSecondary} !important;
  }

  .ant-table-placeholder * {
    color: ${({ theme }) => theme.fgMuted} !important;
  }

  .ant-table-cell-fix-left,
  .ant-table-cell-fix-right {
    background-color: ${({ theme }) => theme.bgCanvas};
  }

  .ant-table-cell {
    color: ${({ theme }) => theme.fgPrimary} !important;
    white-space: nowrap;
  }

  .ant-table-cell::before {
    background-color: ${({ theme }) => theme.bdSubtle} !important;
  }

  .ant-table-wrapper .ant-table-tbody > tr:not(:last-child) > td {
    border-color: ${({ theme }) => theme.bdSubtle};
  }
  .ant-table-wrapper .ant-table-tbody > tr:last-child > td {
    border-bottom: 0;
  }

  .ant-table-column-sort {
    background-color: ${({ theme }) => theme.bgCanvas} !important;
  }

  .ant-table-row:hover .ant-table-cell {
    background: ${({ theme }) => theme.bgMuted} !important;
  }

  .ant-table-thead .ant-table-cell {
    background-color: ${({ theme }) => theme.bgCanvas} !important;
    color: ${({ theme }) => theme.fgPrimary} !important;
  }

  .ant-select-selector {
    background-color: ${({ theme }) => theme.bgCanvas} !important;
    color: ${({ theme }) => theme.fgPrimary} !important;
  }

  .ant-select-arrow {
    color: ${({ theme }) => theme.fgPrimary} !important;
  }

  .ant-pagination-item-link {
    color: ${({ theme }) => theme.fgPrimary} !important;
  }

  .ant-pagination-item {
    border-color: ${({ theme }) => theme.fgPrimary} !important;
    background-color: ${({ theme }) => theme.bgElevated} !important;
    color: ${({ theme }) => theme.fgPrimary} !important;
  }

  .ant-pagination-item-active {
    background-color: ${({ theme }) => theme.fgPrimary} !important;
    color: ${({ theme }) => theme.bgElevated} !important;
  }

  .ant-pagination-item a {
    color: inherit;
  }

  .ant-table-tbody {
    background-color: ${({ theme }) => theme.bgCanvas} !important;
  }

  .ant-table-column-sorter svg {
    fill: ${({ theme }) => theme.fgPrimary} !important ;
  }

  ${({ size }) =>
    size === 'small'
      ? `
  .ant-table-tbody > tr > td {
    padding: 0 8px !important;
    height: 2.5rem;
  }
`
      : ''}

  .ant-pagination {
    gap: 0.2rem;
  }

  .ant-table-body::-webkit-scrollbar {
    width: 1px !important;
    height: 1px !important;
  }
`;

export function AtiraTable<T extends Record<string, any>>({
  data,
  columns,
  loading = false,
  selectedRowKeys,
  onSelectedRowKeysChange,
  ...props
}: AtiraTableProps<T>) {
  const { i18n } = useTranslation();

  const rowSelection: TableRowSelection<T> = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[], selectedRows: T[]) =>
      onSelectedRowKeysChange?.(newSelectedRowKeys),
  };

  return (
    <Flex flexDirection="column" width="100%">
      <StyledTableWrapper size={props.size}>
        <Table
          dataSource={data.map((x, i) => ({
            ...x,
            key: `table-row-${i}`,
          }))}
          rowSelection={props.rowSelection ? rowSelection : undefined}
          columns={columns?.filter((col: any) => col?.data?.enabled !== false)}
          scroll={{ x: 'max-content' }}
          loading={loading}
          style={{ overflowY: 'hidden', height: '100%' }}
          rowHoverable
          direction={i18n.language === AppLangs.AR ? 'rtl' : 'ltr'}
          {...props}
          bordered
        />
      </StyledTableWrapper>
    </Flex>
  );
}
